import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import { Layer } from '../../../../models/layer-settings';
import { debugLog } from './layer-change-coordinator';

export interface FSLBuilder {
  build: (layer: Layer) => Promise<FeatureLayer | GroupLayer | undefined>;
  layerName: (layer: Layer) => string;
}

const layerName = (layerIdPrefix: string, layer: Layer) => `${layerIdPrefix}${layer.id}`;

const fetchFSL = async (url: string) => {
  const params = new URLSearchParams();
  params.append('f', 'json');

  const fullUrl = `${url}?${params}`;
  try {
    const response = await fetch(fullUrl, {
      headers: {
        accept: 'application/json',
      },
      mode: 'cors',
    });

    if (response.ok) {
      // Wait for the JSON to be extracted so we can catch any errors
      const json = await response.json();

      return json;
    }
  } catch (error) {
    if (error instanceof SyntaxError) {
      console.error('Response was not JSON');
    } else {
      console.error('Error fetching data:', error);
    }
  }

  return undefined;
};

const buildLayer = (
  layerIdPrefix: string, layer: Layer,
): Promise<FeatureLayer | GroupLayer | undefined> => new Promise((resolve) => {
  const build = async () => {
    const { url } = layer;
    if (!url) {
      console.error('No URL provided for PublicFSLBuilder');
      resolve(undefined);
      return;
    }

    const urlIncludesSublayerIndex = /\/\d+$/.test(url);
    const json = urlIncludesSublayerIndex ? { layers: [] } : await fetchFSL(url);
    if (!json) {
      console.error(`Not able to fetch from '${url}'`);
      resolve(undefined);
      return;
    }

    let resolvedLayer: FeatureLayer | GroupLayer;
    if (json.layers.length < 2) {
      debugLog('Adding public fsl as FeatureLayer');
      resolvedLayer = new FeatureLayer({ url });
    } else {
      debugLog('Adding public fsl as GroupLayer');
      const sublayers: FeatureLayer[] = json.layers.map((_, index) => {
        const layerUrl = `${url}/${index}`;
        return new FeatureLayer({ url: layerUrl });
      });

      const groupLayer = new GroupLayer();
      groupLayer.addMany(sublayers);
      resolvedLayer = groupLayer;
    }

    resolvedLayer.id = layerName(layerIdPrefix, layer);
    resolve(resolvedLayer);
  };

  build();
});

const PublicFSLBuilder = (layerIdPrefix: string): FSLBuilder => ({
  build: (layer: Layer) => buildLayer(layerIdPrefix, layer),
  layerName: (layer: Layer) => layerName(layerIdPrefix, layer),
});

export default PublicFSLBuilder;
